import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { fixPhotoUrl } from '@/lib/photoUrl';
import { toIsoLocal } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  MapPin, 
  Calendar,
  Users,
  CheckCircle2,
  Loader2,
  Search,
  SlidersHorizontal,
  X,
  Wifi,
  Car,
  Waves,
  Snowflake,
  Tv,
  UtensilsCrossed,
  Dumbbell,
  Dog,
  Bath,
  Mountain,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '@/i18n';

// Common amenities with icons for filtering (labels resolved via i18n key)
const AMENITY_OPTIONS = [
  { id: 'wifi', labelKey: 'browse_page.amenity.wifi', icon: Wifi },
  { id: 'parking', labelKey: 'browse_page.amenity.parking', icon: Car },
  { id: 'pool', labelKey: 'browse_page.amenity.pool', icon: Waves },
  { id: 'air_conditioning', labelKey: 'browse_page.amenity.air_conditioning', icon: Snowflake },
  { id: 'tv', labelKey: 'browse_page.amenity.tv', icon: Tv },
  { id: 'kitchen', labelKey: 'browse_page.amenity.kitchen', icon: UtensilsCrossed },
  { id: 'gym', labelKey: 'browse_page.amenity.gym', icon: Dumbbell },
  { id: 'pet_friendly', labelKey: 'browse_page.amenity.pet_friendly', icon: Dog },
  { id: 'hot_tub', labelKey: 'browse_page.amenity.hot_tub', icon: Bath },
  { id: 'ocean_view', labelKey: 'browse_page.amenity.ocean_view', icon: Mountain },
];

const BrowsePropertiesPage = () => {
  const t = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState({ from: undefined, to: undefined });
  const [guestCount, setGuestCount] = useState(2);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Booking dialog state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [pricingQuote, setPricingQuote] = useState(null);
  const [quotingLoading, setQuotingLoading] = useState(false);

  // Contract signing state
  const [contractTemplate, setContractTemplate] = useState(null);
  const [contractPreview, setContractPreview] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [tenantIdNumber, setTenantIdNumber] = useState('');
  const [contractAgreed, setContractAgreed] = useState(false);
  const [bookingStep, setBookingStep] = useState('details'); // 'details' | 'contract' | 'payment'

  // Guest detail fields (pre-filled from profile, editable)
  const [guestPhone, setGuestPhone] = useState('');
  const [guestIdType, setGuestIdType] = useState('passport');
  const [guestIdNumber, setGuestIdNumber] = useState('');
  const [guestNationality, setGuestNationality] = useState('');

  const isCustomer = user?.role === 'CUSTOMER';
  const isOwner = user?.role === 'OWNER';

  // Fetch contract template status on mount
  useEffect(() => {
    const fetchContractTemplate = async () => {
      try {
        const response = await axios.get(`${API}/contracts/template/active`);
        if (response.data?.has_template) {
          setContractTemplate(response.data.template);
        }
      } catch (error) {
        // No contract template, continue without
      }
    };
    fetchContractTemplate();
  }, []);

  // Filtered properties based on amenities
  const filteredProperties = useMemo(() => {
    if (selectedAmenities.length === 0) return properties;
    
    return properties.filter(prop => {
      const propAmenities = (prop.amenities || []).map(a => a.toLowerCase().replace(/\s+/g, '_'));
      return selectedAmenities.every(selected => 
        propAmenities.some(pa => pa.includes(selected.toLowerCase()))
      );
    });
  }, [properties, selectedAmenities]);

  const nights = selectedDateRange.from && selectedDateRange.to 
    ? Math.ceil((selectedDateRange.to - selectedDateRange.from) / (1000 * 60 * 60 * 24))
    : 0;

  // Search for available properties
  const handleSearch = async () => {
    if (!selectedDateRange.from || !selectedDateRange.to) {
      toast.error(t('browse_page.toast.select_dates'));
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const startDate = toIsoLocal(selectedDateRange.from);
      const endDate = toIsoLocal(selectedDateRange.to);

      // Get all properties first
      const response = await axios.get(`${API}/properties`, {
        params: { search: searchQuery || undefined }
      });

      const allProps = response.data.items || [];
      
      // Filter by availability - check each property
      const availableProps = [];
      
      for (const prop of allProps) {
        try {
          const availRes = await axios.get(`${API}/calendar/availability/${prop.id}`, {
            params: { start_date: startDate, end_date: endDate }
          });
          
          if (availRes.data.is_available) {
            availableProps.push(prop);
          }
        } catch (error) {
          console.error(`Availability check failed for ${prop.id}:`, error);
        }
      }

      setProperties(availableProps);

      if (availableProps.length === 0) {
        toast.info(t('browse_page.toast.none_available'));
      } else {
        toast.success(t('browse_page.toast.found_count', { count: availableProps.length }));
      }
    } catch (error) {
      toast.error(t('browse_page.toast.search_failed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Open booking dialog and fetch pricing
  const openBookingDialog = async (property) => {
    setSelectedProperty(property);
    setShowBookingDialog(true);
    setPricingQuote(null);
    setBookingStep('details');
    setContractAgreed(false);
    setSignatureName('');
    setTenantIdNumber('');
    setContractPreview(null);
    // Pre-fill guest details from profile
    setGuestPhone(user?.phone || '');
    setGuestIdType('passport');
    setGuestIdNumber('');

    if (!isOwner && selectedDateRange.from && selectedDateRange.to) {
      setQuotingLoading(true);
      try {
        const startDate = toIsoLocal(selectedDateRange.from);
        const endDate = toIsoLocal(selectedDateRange.to);

        const response = await axios.post(`${API}/pricing/quote`, {
          property_id: property.id,
          start_date: startDate,
          end_date: endDate,
          payment_method: 'STRIPE'
        });

        setPricingQuote(response.data);
      } catch (error) {
        console.error('Failed to get pricing quote:', error);
        toast.error(t('browse_page.toast.quote_failed'));
      } finally {
        setQuotingLoading(false);
      }
    }
  };

  // Toggle amenity filter
  const toggleAmenity = (amenityId) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityId) 
        ? prev.filter(a => a !== amenityId)
        : [...prev, amenityId]
    );
  };

  // Handle date selection
  const handleDateSelect = (range) => {
    const safeRange = range || { from: undefined, to: undefined };
    setSelectedDateRange(safeRange);
    
    // Close popover when both dates are selected
    if (safeRange.from && safeRange.to) {
      setTimeout(() => setShowDatePicker(false), 300);
    }
  };

  // Handle booking (for owners - free reservation)
  const handleOwnerReservation = async () => {
    if (!selectedProperty || !selectedDateRange.from || !selectedDateRange.to) {
      toast.error(t('browse_page.toast.please_select_dates'));
      return;
    }

    setBookingLoading(true);
    try {
      const startDate = toIsoLocal(selectedDateRange.from);
      const endDate = toIsoLocal(selectedDateRange.to);

      await axios.post(`${API}/bookings`, {
        property_id: selectedProperty.id,
        start_date: startDate,
        end_date: endDate,
        is_owner_reservation: true,
        guest_first_name: user?.name?.split(' ')[0] || 'Owner',
        guest_last_name: user?.name?.split(' ').slice(1).join(' ') || '',
        guest_email: user?.email || '',
        guest_phone: user?.phone || ''
      });

      toast.success(t('browse_page.toast.owner_reservation_ok'));
      setShowBookingDialog(false);
      setSelectedProperty(null);

      // Refresh search results
      handleSearch();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('browse_page.toast.reservation_failed'));
    } finally {
      setBookingLoading(false);
    }
  };

  // Proceed to contract signing step
  const proceedToContract = async () => {
    if (!contractTemplate) {
      // No contract required, skip to payment
      handleCustomerBooking();
      return;
    }
    setBookingStep('contract');
  };

  // Handle customer booking - create booking, sign contract, then redirect to Stripe
  const handleCustomerBooking = async () => {
    if (!selectedProperty || !selectedDateRange.from || !selectedDateRange.to) {
      toast.error(t('browse_page.toast.please_select_dates'));
      return;
    }

    // If contract required and not agreed, show error
    if (contractTemplate && !contractAgreed) {
      toast.error(t('browse_page.toast.sign_to_proceed'));
      return;
    }

    if (contractTemplate && !signatureName.trim()) {
      toast.error(t('browse_page.toast.enter_full_name'));
      return;
    }

    setBookingLoading(true);
    try {
      const startDate = toIsoLocal(selectedDateRange.from);
      const endDate = toIsoLocal(selectedDateRange.to);

      // First create the booking
      const bookingResponse = await axios.post(`${API}/bookings`, {
        property_id: selectedProperty.id,
        start_date: startDate,
        end_date: endDate,
        is_owner_reservation: false,
        guest_first_name: user?.name?.split(' ')[0] || 'Guest',
        guest_last_name: user?.name?.split(' ').slice(1).join(' ') || '',
        guest_email: user?.email || '',
        guest_phone: guestPhone || user?.phone || '',
        guest_id_type: guestIdType || 'passport',
        guest_id_number: guestIdNumber || tenantIdNumber || '',
        guest_nationality: guestNationality || '',
      });

      const booking = bookingResponse.data;

      // If contract template exists, sign the contract
      if (contractTemplate && signatureName.trim()) {
        try {
          await axios.post(`${API}/contracts/sign`, {
            booking_id: booking.id,
            signature_name: signatureName.trim(),
            signature_type: 'full_name',
            tenant_id_number: tenantIdNumber || undefined
          });
        } catch (contractError) {
          console.error('Contract signing failed:', contractError);
          // Continue with booking even if contract fails
        }
      }

      // Then create Stripe checkout session for deposit
      const checkoutResponse = await axios.post(`${API}/stripe/create-checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
        payment_type: 'deposit'
      });

      // Redirect to Stripe checkout
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('browse_page.toast.booking_failed'));
      setBookingLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="browse-properties-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">
          {isOwner ? t('browse_page.header.owner_title') : t('browse_page.header.customer_title')}
        </h1>
        <p className="text-[#194a50]/70 mt-1">
          {isOwner ? t('browse_page.header.owner_subtitle') : t('browse_page.header.customer_subtitle')}
        </p>
      </div>

      {/* Search Bar */}
      <Card className="shadow-sm border-[#194a50]/5">
        <CardContent className="py-5">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Location/Search Input */}
            <div className="flex-1">
              <Label className="text-xs text-[#194a50]/60 mb-1 block">{t('browse_page.label.location')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#194a50]/50" />
                <Input
                  placeholder={t('browse_page.placeholder.location')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-[#194a50]/20 h-11"
                  data-testid="search-location-input"
                />
              </div>
            </div>

            {/* Date Range - Popup */}
            <div className="flex-1">
              <Label className="text-xs text-[#194a50]/60 mb-1 block">{t('browse_page.label.dates')}</Label>
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal h-11 border-[#194a50]/20 ${
                      !selectedDateRange.from ? 'text-muted-foreground' : ''
                    }`}
                    data-testid="date-picker-trigger"
                  >
                    <Calendar className="mr-2 h-4 w-4 text-[#194a50]/50" />
                    {selectedDateRange.from && selectedDateRange.to ? (
                      <span>
                        {selectedDateRange.from.toLocaleDateString()} - {selectedDateRange.to.toLocaleDateString()}
                        <span className="text-[#194a50]/50 ml-1">({t('browse_page.nights_paren', { n: nights })})</span>
                      </span>
                    ) : (
                      <span>{t('browse_page.select_dates')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <CalendarComponent
                    mode="range"
                    selected={selectedDateRange}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                    className="rounded-md"
                    data-testid="date-picker-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Guests */}
            <div className="w-32">
              <Label className="text-xs text-[#194a50]/60 mb-1 block">{t('browse_page.label.guests')}</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#194a50]/50" />
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={guestCount}
                  onChange={(e) => setGuestCount(parseInt(e.target.value) || 1)}
                  className="pl-10 border-[#194a50]/20 h-11"
                  data-testid="guest-count-input"
                />
              </div>
            </div>

            {/* Advanced Filters Button */}
            <div className="flex items-end">
              <Button
                variant="outline"
                size="icon"
                className={`h-11 w-11 border-[#194a50]/20 ${showAdvanced ? 'bg-[#194a50] text-white' : ''}`}
                onClick={() => setShowAdvanced(!showAdvanced)}
                data-testid="advanced-filters-btn"
              >
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <Button
                className="h-11 px-8 bg-[#194a50] hover:bg-[#10555e]"
                onClick={handleSearch}
                disabled={loading || !selectedDateRange.from || !selectedDateRange.to}
                data-testid="search-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {t('common.search')}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvanced && (
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium text-[#0c2431]">{t('browse_page.filter_by_amenities')}</Label>
                {selectedAmenities.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedAmenities([])}
                    className="text-xs text-[#194a50]/70"
                  >
                    {t('browse_page.clear_all')}
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {AMENITY_OPTIONS.map((amenity) => {
                  const Icon = amenity.icon;
                  const isSelected = selectedAmenities.includes(amenity.id);
                  return (
                    <button
                      key={amenity.id}
                      onClick={() => toggleAmenity(amenity.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-[#194a50] bg-[#194a50]/5 text-[#194a50]'
                          : 'border-[#194a50]/10 hover:border-[#194a50]/30 text-[#194a50]/70'
                      }`}
                      data-testid={`amenity-filter-${amenity.id}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{t(amenity.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {hasSearched && (
        <>
          {/* Results count */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#0c2431]">
              {filteredProperties.length === 1
                ? t('browse_page.results.count_one', { count: filteredProperties.length })
                : t('browse_page.results.count_other', { count: filteredProperties.length })}
            </h2>
            {selectedAmenities.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#194a50]/60">{t('browse_page.filtered_by')}</span>
                {selectedAmenities.map(id => {
                  const amenity = AMENITY_OPTIONS.find(a => a.id === id);
                  return (
                    <Badge key={id} variant="secondary" className="flex items-center gap-1">
                      {amenity ? t(amenity.labelKey) : id}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => toggleAmenity(id)} />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* Properties Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProperties.map((property) => (
              <Card 
                key={property.id} 
                className="shadow-sm border-[#194a50]/5 hover:shadow-md transition-shadow overflow-hidden"
                data-testid={`property-card-${property.id}`}
              >
                {/* Property Image */}
                <div className="h-48 bg-gradient-to-br from-[#194a50]/20 to-[#d8a28c]/20 flex items-center justify-center">
                  {property.photos?.[0] ? (
                    <img
                      src={fixPhotoUrl(property.photos[0])}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 className="w-16 h-16 text-[#194a50]/30" />
                  )}
                </div>
                
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg text-[#0c2431] line-clamp-1">
                      {property.title}
                    </h3>
                    {property.reference && (
                      <Badge variant="outline" className="text-xs">
                        {property.reference}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 text-[#194a50]/70 text-sm mb-3">
                    <MapPin className="w-4 h-4" />
                    {property.location?.city}, {property.location?.country}
                  </div>

                  {/* Amenities */}
                  {property.amenities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {property.amenities.slice(0, 3).map((amenity) => (
                        <span key={amenity} className="text-xs bg-[#e1dad2] text-[#194a50] px-2 py-0.5 rounded">
                          {amenity}
                        </span>
                      ))}
                      {property.amenities.length > 3 && (
                        <span className="text-xs text-[#194a50]/60">+{property.amenities.length - 3}</span>
                      )}
                    </div>
                  )}

                  <p className="text-sm text-[#194a50]/60 line-clamp-2 mb-4">
                    {property.description}
                  </p>

                  <div className="flex items-center justify-between pt-3 border-t border-[#194a50]/10">
                    <div>
                      <span className="font-semibold text-[#194a50]">€{property.base_nightly_rate}</span>
                      <span className="text-xs text-[#194a50]/60">{t('browse_page.slash_night')}</span>
                    </div>
                    <Button
                      size="sm"
                      className="bg-[#194a50] hover:bg-[#10555e]"
                      onClick={() => openBookingDialog(property)}
                      data-testid={`book-property-${property.id}`}
                    >
                      {isOwner ? t('browse_page.reserve') : t('browse_page.book_now')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProperties.length === 0 && (
            <Card className="shadow-sm border-[#194a50]/5">
              <CardContent className="py-16 text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-[#194a50]/30" />
                <h3 className="text-xl font-semibold text-[#0c2431] mb-2">{t('browse_page.empty.title')}</h3>
                <p className="text-[#194a50]/60">
                  {selectedAmenities.length > 0
                    ? t('browse_page.empty.remove_filters')
                    : t('browse_page.empty.try_dates')}
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Initial State */}
      {!hasSearched && (
        <Card className="shadow-sm border-[#194a50]/5">
          <CardContent className="py-16 text-center">
            <Search className="w-16 h-16 mx-auto mb-4 text-[#194a50]/30" />
            <h3 className="text-xl font-semibold text-[#0c2431] mb-2">
              {isOwner ? t('browse_page.initial.owner_title') : t('browse_page.initial.customer_title')}
            </h3>
            <p className="text-[#194a50]/60 max-w-md mx-auto">
              {isOwner
                ? t('browse_page.initial.owner_body')
                : t('browse_page.initial.customer_body')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">
              {isOwner ? t('browse_page.dialog.owner_title') : t('browse_page.dialog.customer_title')}
            </DialogTitle>
          </DialogHeader>

          {selectedProperty && (
            <div className="space-y-4 mt-4">
              {/* Property Info */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-[#194a50]/20 to-[#d8a28c]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {selectedProperty.photos?.[0] ? (
                    <img
                      src={fixPhotoUrl(selectedProperty.photos[0])}
                      alt={selectedProperty.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 className="w-8 h-8 text-[#194a50]/30" />
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-[#0c2431]">{selectedProperty.title}</h4>
                  <p className="text-sm text-[#194a50]/70 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {selectedProperty.location?.city}, {selectedProperty.location?.country}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="p-4 border rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-[#194a50]/60">{t('browse_page.check_in')}</p>
                    <p className="font-semibold text-[#0c2431]">
                      {selectedDateRange.from?.toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#194a50]">{nights}</p>
                    <p className="text-xs text-[#194a50]/60">{t('browse_page.nights')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#194a50]/60">{t('browse_page.check_out')}</p>
                    <p className="font-semibold text-[#0c2431]">
                      {selectedDateRange.to?.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Owner Reservation Notice */}
              {isOwner && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>{t('browse_page.owner_notice.label')}</strong> {t('browse_page.owner_notice.body')}
                  </p>
                </div>
              )}

              {/* Guest Details (customer only, details step) */}
              {isCustomer && bookingStep === 'details' && (
                <div className="space-y-3 p-4 border rounded-lg bg-gray-50/60">
                  <h4 className="font-semibold text-sm text-[#0c2431]">{t('browse_page.guest.title')}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#194a50]/60">{t('browse_page.guest.phone')} <span className="text-red-500">*</span></Label>
                      <Input
                        placeholder={t('browse_page.guest.phone_placeholder')}
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#194a50]/60">{t('browse_page.guest.id_type')} <span className="text-red-500">*</span></Label>
                      <select
                        value={guestIdType}
                        onChange={(e) => setGuestIdType(e.target.value)}
                        className="w-full h-8 text-sm rounded-md border border-input bg-background px-3 focus:outline-none focus:ring-1 focus:ring-[#194a50]/40"
                      >
                        <option value="passport">{t('browse_page.guest.id_passport')}</option>
                        <option value="national_id">{t('browse_page.guest.id_national')}</option>
                        <option value="driver_license">{t('browse_page.guest.id_driver')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#194a50]/60">{t('browse_page.guest.id_number')} <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder={t('browse_page.guest.id_number_placeholder')}
                      value={guestIdNumber}
                      onChange={(e) => setGuestIdNumber(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#194a50]/60">{t('browse_page.guest.nationality')}</Label>
                    <Input
                      placeholder={t('browse_page.guest.nationality_placeholder')}
                      value={guestNationality}
                      onChange={(e) => setGuestNationality(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Price Summary for Customers */}
              {isCustomer && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold text-[#0c2431] mb-3">{t('browse_page.price.title')}</h4>

                  {quotingLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-[#194a50]" />
                      <span className="ml-2 text-[#194a50]/60">{t('browse_page.price.calculating')}</span>
                    </div>
                  ) : pricingQuote?.price_breakdown ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#194a50]/70">
                          €{pricingQuote.price_breakdown.applied_rate_per_night} × {t('browse_page.price.nights_count', { n: nights })}
                        </span>
                        <span className="text-[#0c2431]">
                          €{pricingQuote.price_breakdown.accommodation_subtotal?.toLocaleString()}
                        </span>
                      </div>
                      {pricingQuote.price_breakdown.cleaning_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[#194a50]/70">{t('browse_page.price.cleaning_fee')}</span>
                          <span className="text-[#0c2431]">€{pricingQuote.price_breakdown.cleaning_fee}</span>
                        </div>
                      )}
                      {pricingQuote.price_breakdown.stripe_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-[#194a50]/70">{t('browse_page.price.processing_fee')}</span>
                          <span className="text-[#0c2431]">€{pricingQuote.price_breakdown.stripe_fee}</span>
                        </div>
                      )}
                      {pricingQuote.price_breakdown.security_deposit > 0 && (
                        <div className="flex justify-between bg-amber-50 p-2 rounded">
                          <span className="text-amber-800">{t('browse_page.price.security_deposit')}</span>
                          <span className="text-amber-900 font-medium">€{pricingQuote.price_breakdown.security_deposit?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t font-semibold">
                        <span className="text-[#0c2431]">{t('browse_page.price.total')}</span>
                        <span className="text-[#194a50]">
                          €{pricingQuote.total_amount?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-green-700 bg-green-50 p-2 rounded mt-2">
                        <span>{t('browse_page.price.deposit_due')}</span>
                        <span className="font-semibold">
                          €{pricingQuote.deposit_amount?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#194a50]/70">
                          €{selectedProperty.base_nightly_rate} × {t('browse_page.price.nights_count', { n: nights })}
                        </span>
                        <span className="text-[#0c2431]">
                          €{(selectedProperty.base_nightly_rate * nights).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-[#194a50]/50 text-center">
                        {t('browse_page.price.final_at_checkout')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Owner Price Summary */}
              {isOwner && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold text-[#0c2431] mb-3">{t('browse_page.price.title')}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#194a50]/70">
                        €{selectedProperty.base_nightly_rate} × {t('browse_page.price.nights_count', { n: nights })}
                      </span>
                      <span className="text-[#0c2431] line-through">
                        €{(selectedProperty.base_nightly_rate * nights).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>{t('browse_page.price.owner_discount')}</span>
                      <span>-100%</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t font-semibold">
                      <span className="text-[#0c2431]">{t('browse_page.price.total')}</span>
                      <span className="text-[#194a50]">€0</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Contract Signing Section (Customer only, when template exists) */}
              {isCustomer && contractTemplate && bookingStep === 'contract' && (
                <div className="space-y-4 p-4 border rounded-lg bg-amber-50/50">
                  <div className="flex items-center gap-2 text-amber-800">
                    <FileText className="w-5 h-5" />
                    <h4 className="font-semibold">{t('browse_page.contract.title')}</h4>
                  </div>

                  <p className="text-sm text-[#194a50]/70">
                    {t('browse_page.contract.intro')}
                  </p>

                  {/* Contract Fields Preview */}
                  <div className="bg-white rounded-lg p-3 text-sm space-y-2 border">
                    <div className="flex justify-between">
                      <span className="text-[#194a50]/60">{t('browse_page.contract.property')}</span>
                      <span className="font-medium">{selectedProperty.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#194a50]/60">{t('browse_page.contract.check_in')}</span>
                      <span>{selectedDateRange.from?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#194a50]/60">{t('browse_page.contract.check_out')}</span>
                      <span>{selectedDateRange.to?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#194a50]/60">{t('browse_page.contract.total_amount')}</span>
                      <span>€{pricingQuote?.total_amount?.toLocaleString() || '—'}</span>
                    </div>
                  </div>

                  {/* ID Number Input (pre-filled from details step) */}
                  <div>
                    <Label className="text-sm">{t('browse_page.contract.id_label')}</Label>
                    <Input
                      placeholder={t('browse_page.contract.id_placeholder')}
                      value={guestIdNumber || tenantIdNumber}
                      onChange={(e) => { setGuestIdNumber(e.target.value); setTenantIdNumber(e.target.value); }}
                      className="mt-1"
                      data-testid="tenant-id-input"
                    />
                    <p className="text-xs text-[#194a50]/50 mt-1">{t('browse_page.contract.id_hint')}</p>
                  </div>

                  {/* Signature Input */}
                  <div>
                    <Label className="text-sm">{t('browse_page.contract.sign_label')}</Label>
                    <Input
                      placeholder={t('browse_page.contract.sign_placeholder')}
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      className="mt-1 font-serif italic"
                      data-testid="signature-input"
                    />
                  </div>

                  {/* Agreement Checkbox */}
                  <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
                    <input
                      type="checkbox"
                      id="contract-agree"
                      checked={contractAgreed}
                      onChange={(e) => setContractAgreed(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#194a50] focus:ring-[#194a50]"
                      data-testid="contract-agree-checkbox"
                    />
                    <label htmlFor="contract-agree" className="text-sm text-[#194a50]/80">
                      {t('browse_page.contract.agree_text')}
                    </label>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  if (bookingStep === 'contract') {
                    setBookingStep('details');
                  } else {
                    setShowBookingDialog(false);
                  }
                }}>
                  {bookingStep === 'contract' ? t('common.back') : t('common.cancel')}
                </Button>

                {isOwner ? (
                  <Button
                    className="bg-[#194a50] hover:bg-[#10555e]"
                    disabled={bookingLoading}
                    onClick={handleOwnerReservation}
                    data-testid="confirm-booking-btn"
                  >
                    {bookingLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('browse_page.action.creating')}
                      </>
                    ) : (
                      t('browse_page.action.create_reservation')
                    )}
                  </Button>
                ) : bookingStep === 'details' && contractTemplate ? (
                  <Button
                    className="bg-[#194a50] hover:bg-[#10555e]"
                    disabled={!guestPhone.trim() || !guestIdNumber.trim()}
                    onClick={() => setBookingStep('contract')}
                    data-testid="proceed-to-contract-btn"
                  >
                    {t('browse_page.action.continue_to_contract')}
                  </Button>
                ) : (
                  <Button
                    className="bg-[#194a50] hover:bg-[#10555e]"
                    disabled={bookingLoading || !guestPhone.trim() || !guestIdNumber.trim() || (contractTemplate && (!contractAgreed || !signatureName.trim()))}
                    onClick={handleCustomerBooking}
                    data-testid="confirm-booking-btn"
                  >
                    {bookingLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('browse_page.action.processing')}
                      </>
                    ) : (
                      t('browse_page.action.pay_deposit_book')
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrowsePropertiesPage;
